import { useContext, useEffect, useState } from 'react';
import {
  HiOutlineGlobeAlt,
  HiOutlineNewspaper,
  HiOutlineOfficeBuilding,
  HiOutlinePencil,
} from 'react-icons/hi';
import { deleteReward, postReward, putReward } from '../../../apis/profile.api';
import {
  handleCheckChangeForm,
  handleCheckFalsyForm,
} from '../../../common/checkForm';
import {
  handleDeleteForm,
  handleResetForm,
  handleSubmitForm,
} from '../../../common/formCallApi';
import { renderSubmitFormContent } from '../../../common/formCommonFunc';
import {
  listViewPermissionIcon,
  viewPermissionData,
} from '../../../constant/user-setting-modal-resources';
import { checkLinkConvention } from '../../../helpers/validate';
import { IModalProps as IProps } from '../../../interfaces/components/modal.interface';
import { IRewardInput as IUserRewardValue } from '../../../interfaces/profile.interface';
import FormLayout from '../../../layouts/FormLayout/FormLayout2';
import AuthContext from '../../../state/auth/AuthContext';
import { useModal } from '../../../state/modal-provider/ModalProvider';
import { initRewardState } from '../../../state/user-setting-modal/initState';
import DateInput from '../../input/DateInput';
import PrimaryInput from '../../input/PrimaryInput';
import PrimaryTextArea from '../../input/PrimaryTextArea';
import SelectInput from '../../input/SelectInput2';

const requiredKeys = ['rewardName', 'organization'];

const UserAwardModal = ({
  defaultData,
  cancelCallback,
  isModalCancelled,
  setIsModalCancelled,
}: IProps) => {
  const [userRewardValue, setUserRewardValue] = useState<IUserRewardValue>({
    rewardName: defaultData?.rewardName ?? '',
    organization: defaultData?.organization ?? '',
    grantDate: defaultData?.grantDate ?? '',
    link: defaultData?.link ?? '',
    description: defaultData?.description ?? '',
    viewPermission: defaultData?.viewPermission ?? 'public',
  });

  const [isSubmit, setIsSubmit] = useState<boolean>(false);

  const { setLoading } = useContext(AuthContext);
  const { toast } = useModal();

  const handleSetState = (name: string, value: string) => {
    setUserRewardValue((state) => ({ ...state, [name]: value }));
  };

  const handleToggleLeaveModal = () => {
    if (
      !handleCheckChangeForm(userRewardValue, {
        rewardName: defaultData?.rewardName ?? '',
        organization: defaultData?.organization ?? '',
        grantDate: defaultData?.grantDate ?? '',
        link: defaultData?.link ?? '',
        description: defaultData?.description ?? '',
        viewPermission: defaultData?.viewPermission ?? 'public',
      })
    ) {
      toast.warning({
        deleteTitle: 'Thoát',
        cancelTitle: 'Ở lại',
        onDelete: () => cancelCallback && cancelCallback(false),
        onCancel: () => setIsModalCancelled && setIsModalCancelled(false),
        title: 'Thay đổi chưa lưu',
        content:
          'Thay đổi của bạn chưa được lưu. Bạn vẫn muốn thoát mà không lưu?',
      });
    } else {
      cancelCallback && cancelCallback(false);
    }
  };

  const resetForm = () =>
    handleResetForm({
      setValueState: setUserRewardValue,
      initValueState: initRewardState,
    });

  const deleteForm = () =>
    handleDeleteForm({
      runAsyncFunc: !!defaultData?._id,
      callback: () => setIsSubmit(false),
      asyncFunc: deleteReward,
      handleResetForm: resetForm,
      id: defaultData?._id ?? '',
      formCancelCallBack: cancelCallback,
      toggleSuccessModalNoti: () =>
        toast.success({
          content: 'Xoá thông tin thành công.',
        }),
      toggleErrorModalNoti: () =>
        toast.error({
          content: 'Có lỗi xảy ra, vui lòng thử lại.',
        }),
      setLoading,
    });

  const validateLink = () =>
    // link is not required
    userRewardValue.link ? checkLinkConvention(userRewardValue.link) : true;

  const submitForm = () =>
    handleSubmitForm({
      callback: () => setIsSubmit(true),
      extraValidate: validateLink,
      requiredKeys,
      data: userRewardValue,
      id: defaultData?._id ?? '',
      asyncFunc: !defaultData?._id ? postReward : putReward,
      formCancelCallBack: cancelCallback,
      toggleSuccessModalNoti: () =>
        toast.success({
          content: renderSubmitFormContent(defaultData?._id),
        }),
      toggleErrorModalNoti: () =>
        toast.error({
          content: 'Có lỗi xảy ra, vui lòng thử lại.',
        }),
      setLoading,
    });

  // warning when leave modal
  useEffect(() => {
    if (isModalCancelled) {
      handleToggleLeaveModal();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isModalCancelled]);

  return (
    <FormLayout
      deleteCallback={() =>
        toast.warning({
          title: 'Xoá mục giải thưởng!',
          content: 'Bạn chắc chắn muốn xoá nội dung mục giải thưởng?',
          deleteTitle: 'Xoá',
          cancelTitle: 'Huỷ',
          onDelete: deleteForm,
        })
      }
      saveCallback={submitForm}
      formName="GIẢI THƯỞNG"
      cancelCallback={handleToggleLeaveModal}
      disabledDeletButton={handleCheckFalsyForm(userRewardValue)}
    >
      <div className="sm:py-4 flex flex-col justify-center gap-7">
        <PrimaryInput
          type="text"
          desc="Tên giải thưởng*"
          value={userRewardValue.rewardName}
          setValue={handleSetState}
          isObject
          inputName="rewardName"
          icon={<HiOutlineNewspaper />}
          error={isSubmit && !userRewardValue.rewardName}
        />
        {isSubmit && !userRewardValue.rewardName && (
          <p className="text-red-400 -mt-5 -mb-3 text-sm">
            Vui lòng điền tên giải thưởng của bạn
          </p>
        )}
        <PrimaryInput
          type="text"
          desc="Tổ chức*"
          value={userRewardValue.organization}
          setValue={handleSetState}
          isObject
          inputName="organization"
          icon={<HiOutlineOfficeBuilding />}
          error={isSubmit && !userRewardValue.organization}
        />
        {isSubmit && !userRewardValue.organization && (
          <p className="text-red-400 -mt-5 -mb-3 text-sm">
            Vui lòng điền tên tổ chức trao giải thưởng cho bạn
          </p>
        )}
        <DateInput
          value={userRewardValue.grantDate}
          setValue={handleSetState}
          placeholder="Ngày nhận"
          isObject
          name="grantDate"
        />
        <PrimaryInput
          type="text"
          desc="Liên kết"
          value={userRewardValue.link}
          setValue={handleSetState}
          isObject
          inputName="link"
          icon={<HiOutlineGlobeAlt />}
          error={isSubmit && !validateLink()}
        />
        {isSubmit && !validateLink() && (
          <p className="text-red-400 -mt-5 -mb-3 text-sm">
            Link sai định dạng, vui lòng kiểm tra lại
          </p>
        )}
        <PrimaryTextArea
          type="text"
          desc="Mô tả"
          value={userRewardValue.description}
          setValue={handleSetState}
          isObject
          inputName="description"
          icon={<HiOutlinePencil />}
        />
        <SelectInput
          data={viewPermissionData}
          placeholder="Công khai"
          setValue={handleSetState}
          value={userRewardValue.viewPermission}
          error={false}
          propertyName="viewPermission"
          isObject
          listIcon={listViewPermissionIcon}
        />
        <span className="text-[#979797] font-normal text-base">
          *Hãy điền đầy đủ các thông tin để tăng tính chuyên nghiệp cho trang
          của bạn.
        </span>
      </div>
    </FormLayout>
  );
};

export default UserAwardModal;
